import { useGlobalContext } from 'GlobalContext';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createResellerContract } from 'services/reseller';
import { loadSoftwaresAndServices } from 'services/software';
import { Service } from 'shared/models/service';
import { Software } from 'shared/models/software';
import { onlyDocumentChars } from 'shared/helpers/string';
import { Periodicity } from 'shared/types/periodicity';
import CreateResellerContractModal from './CreateResellerContractModal';

export interface ContractErrors {
  due_date?: string;
  services?: string;
}

export interface InstallationErrors {
  document?: string;
  password?: string;
  confirmPassword?: string;
  negocio?: string;
}

interface ModalContextData {
  step: number;
  setStep(n: number): void;
  nextStep(): void;
  previousStep(): void;
  loading: boolean;

  // Contract
  paymentDay: number;
  setPaymentDay(n: number): void;
  periodicity: Periodicity;
  setPeriodicity(p: Periodicity): void;
  product?: Software;
  services: Service[];
  selectedServiceIds: number[];
  addService(id: number): void;
  removeService(id: number): void;
  contractErrors: ContractErrors;

  // Installation
  document: string;
  setDocument(s: string): void;
  password: string;
  setPassword(s: string): void;
  confirmPassword: string;
  setConfirmPassword(s: string): void;
  negocio: string;
  setNegocio(s: string): void;
  installationErrors: InstallationErrors;

  validateContractStep(): boolean;
  handleSubmit(): void;
}

const Context = createContext<ModalContextData>({} as ModalContextData);

export const useCreateResellerContractModalContext = () => useContext(Context);

interface Props {
  isOpen: boolean;
  onClose(): void;
}

const CreateResellerContractModalContext: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { confirm } = useGlobalContext();
  const { customer, loadCustomer } = useCustomerContext();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Contract
  const [paymentDay, setPaymentDay] = useState(5);
  const [periodicity, setPeriodicity] = useState<Periodicity>('yearly');
  const [product, setProduct] = useState<Software>();
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [contractErrors, setContractErrors] = useState<ContractErrors>({});

  // Installation
  const [document, setDocument] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [negocio, setNegocio] = useState('');
  const [installationErrors, setInstallationErrors] =
    useState<InstallationErrors>({});

  const nextStep = () => setStep((s) => s + 1);
  const previousStep = () => setStep((s) => s - 1);

  const addService = (id: number) => {
    if (!selectedServiceIds.includes(id)) {
      setSelectedServiceIds([...selectedServiceIds, id]);
    }
  };

  const removeService = (id: number) => {
    setSelectedServiceIds(selectedServiceIds.filter((sid) => sid !== id));
  };

  const filteredServices = product
    ? (product.services ?? []).filter(
        (s: Service) => s.periodicity === periodicity,
      )
    : [];

  useEffect(() => {
    if (!isOpen) return;
    loadSoftwaresAndServices()
      .then((response) => {
        const softwares =
          response.data?.results?.map((s) => {
            const sw = new Software(s);
            sw.services = sw.services.filter((svc) =>
              svc.name?.toLowerCase().includes('revenda'),
            );
            return sw;
          }) ?? [];
        const withServices = softwares.filter((sw) => sw.services.length > 0);
        if (withServices.length) {
          setProduct(withServices[0]);
        }
      })
      .catch(() => toast.error('Falha ao carregar serviços'));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(0);
      setPaymentDay(5);
      setPeriodicity('yearly');
      setSelectedServiceIds([]);
      setContractErrors({});
      setDocument('');
      setPassword('');
      setConfirmPassword('');
      setNegocio('');
      setInstallationErrors({});
      setLoading(false);
    }
  }, [isOpen]);

  const validateContractStep = (): boolean => {
    const errors: ContractErrors = {};
    if (!paymentDay) errors.due_date = 'Dia de vencimento é obrigatório';
    if (!selectedServiceIds.length)
      errors.services = 'Selecione ao menos um serviço';
    setContractErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateInstallationStep = (): boolean => {
    const errors: InstallationErrors = {};
    if (!document) errors.document = 'Documento é obrigatório';
    if (!password) errors.password = 'Senha é obrigatória';
    else if (password.length < 6)
      errors.password = 'Senha deve ter ao menos 6 caracteres';
    if (!confirmPassword) errors.confirmPassword = 'Confirmação é obrigatória';
    else if (password !== confirmPassword)
      errors.confirmPassword = 'As senhas não conferem';
    if (!negocio) errors.negocio = 'Tipo do estabelecimento é obrigatório';
    setInstallationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateInstallationStep()) return;
    if (loading || !customer?.id) return;

    const payload = {
      customer_id: customer.id,
      contract: {
        due_date: (() => {
          const today = new Date();
          const dueDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            paymentDay,
          );
          if (paymentDay < today.getDate()) {
            dueDate.setMonth(dueDate.getMonth() + 1);
          }
          const y = dueDate.getFullYear();
          const m = String(dueDate.getMonth() + 1).padStart(2, '0');
          const d = String(dueDate.getDate()).padStart(2, '0');
          return `${y}-${m}-${d} 00:00:00`;
        })(),
        payment_method: 'credit-card',
        periodicity,
        services: selectedServiceIds.map((id) => ({ service_id: id })),
      },
      installation: {
        document: onlyDocumentChars(document),
        password,
        negocio,
        permission: null,
      },
    };

    confirm('Criar contrato de revenda', 'Deseja continuar?', async () => {
      setLoading(true);
      try {
        await createResellerContract(payload);
        toast.success('Contrato criado com sucesso');
        onClose();
        loadCustomer();
      } catch {
        toast.error('Falha ao criar contrato');
      } finally {
        setLoading(false);
      }
    });
  };

  const data: ModalContextData = {
    step,
    setStep,
    nextStep,
    previousStep,
    loading,
    paymentDay,
    setPaymentDay,
    periodicity,
    setPeriodicity,
    product,
    services: filteredServices,
    selectedServiceIds,
    addService,
    removeService,
    contractErrors,
    document,
    setDocument,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    negocio,
    setNegocio,
    installationErrors,
    validateContractStep,
    handleSubmit,
  };

  return (
    <Context.Provider value={data}>
      <CreateResellerContractModal isOpen={isOpen} onClose={onClose} />
    </Context.Provider>
  );
};

export default CreateResellerContractModalContext;
